import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, CreditCard, DollarSign, RotateCcw, ArrowLeft, Save, Trash2, LayoutGrid, Package, Plus, Percent } from "lucide-react";
import { Logo } from "@/components/Logo";
import { HoloCard, NeonButton } from "@/components/Holo";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const TABS = [["stats", "Overview"], ["users", "Users"], ["plans", "Pricing"], ["packs", "Credit Packs"], ["settings", "Settings"], ["refunds", "Refunds"]];

export default function Admin() {
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [settings, setSettings] = useState({ promo_bonus_pct: 0, rollover_enabled: true });
  const [refunds, setRefunds] = useState([]);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const loadAll = async () => {
    try {
      const [s, u, p, pk, st, r] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/plans"),
        api.get("/admin/packs"), api.get("/admin/settings"), api.get("/admin/refunds"),
      ]);
      setStats(s.data); setUsers(u.data); setPlans(p.data); setPacks(pk.data); setSettings(st.data); setRefunds(r.data);
    } catch (e) { toast.error("Failed to load admin data"); }
  };
  useEffect(() => { loadAll(); }, []);

  const saveUser = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { monthly_credits: Number(u.monthly_credits), purchased_credits: Number(u.purchased_credits), plan: u.plan, role: u.role });
      toast.success("User updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delUser = async (id) => {
    try { await api.delete(`/admin/users/${id}`); setUsers((p) => p.filter((x) => x.id !== id)); toast("User removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const savePlan = async (pl) => {
    try {
      await api.put(`/admin/plans/${pl.id}`, {
        name: pl.name, price: Number(pl.price), credits: Number(pl.credits),
        features: typeof pl.features === "string" ? pl.features.split("\n").filter(Boolean) : pl.features,
        accent: pl.accent, highlight: pl.highlight, order: pl.order, price_label: pl.price_label,
      });
      toast.success("Plan updated"); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const savePack = async (pk) => {
    try {
      await api.put(`/admin/packs/${pk.id}`, { name: pk.name, price: Number(pk.price), credits: Number(pk.credits), multiplier: pk.multiplier, active: pk.active, order: Number(pk.order) });
      toast.success("Pack updated"); loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addPack = async () => {
    try { await api.post("/admin/packs", { name: "New Pack", price: 10, credits: 500, multiplier: "1x", active: true, order: packs.length + 1 }); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delPack = async (id) => {
    try { await api.delete(`/admin/packs/${id}`); setPacks((p) => p.filter((x) => x.id !== id)); toast("Pack removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveSettings = async () => {
    try { await api.put("/admin/settings", { promo_bonus_pct: Number(settings.promo_bonus_pct), rollover_enabled: settings.rollover_enabled }); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendReengage = async (type) => {
    try { const { data } = await api.post("/admin/reengagement", { type }); toast.success(`Campaign sent to ${data.sent} creators`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const upd = (setter) => (id, field, val) => setter((arr) => arr.map((x) => x.id === id ? { ...x, [field]: val } : x));

  return (
    <div className="min-h-screen bg-[#050505] grain text-white">
      <nav className="h-16 glass border-b border-white/10 flex items-center justify-between px-5 sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <Logo variant="neon" className="h-7" />
          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neon-yellow border border-neon-yellow/40 px-2 py-0.5">Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <NeonButton accent="turquoise" onClick={() => navigate("/app")} data-testid="admin-to-app"><LayoutGrid size={13} /> Command Center</NeonButton>
          <NeonButton accent="pink" onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout"><ArrowLeft size={13} /> Exit</NeonButton>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6 sm:p-10">
        <h1 className="font-display font-bold text-2xl sm:text-3xl">FUTRA Control Grid</h1>
        <div className="flex flex-wrap gap-2 mt-6 mb-8">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`font-mono-tech text-[11px] uppercase tracking-widest px-4 py-2 border transition-colors ${tab === k ? "border-neon-turquoise text-neon-turquoise" : "border-white/15 text-white/50"}`}>{l}</button>
          ))}
        </div>

        {tab === "stats" && stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-stats">
            {[[Users, "Total Users", stats.total_users, "turquoise"], [CreditCard, "Total Renders", stats.total_generations, "pink"], [DollarSign, "MRR", "$" + stats.mrr, "green"], [RotateCcw, "Credits Refunded", stats.total_refunded, "purple"]].map(([Icon, l, v, a]) => (
              <HoloCard key={l} accent={a} className="p-6" testid={`admin-stat-${l}`}>
                <Icon size={18} style={{ color: `var(--${a})` }} className="mb-3" />
                <div className="font-display font-black text-3xl">{v}</div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40 mt-1">{l}</div>
              </HoloCard>
            ))}
            <div className="col-span-2 lg:col-span-4 mt-2">
              <h2 className="font-mono-tech text-white/50 text-xs uppercase tracking-widest mb-3">// Users by Plan</h2>
              <div className="flex flex-wrap gap-3">
                {stats.users_by_plan.map((b) => (
                  <span key={b.plan} className="font-mono-tech text-sm border border-white/10 px-4 py-2">{b.plan}: <span className="text-neon-turquoise">{b.count}</span></span>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "users" && (
          <HoloCard accent="turquoise" className="overflow-x-auto" hover={false} testid="admin-users">
            <table className="w-full font-mono-tech text-xs min-w-[720px]">
              <thead><tr className="text-left text-white/40 uppercase tracking-widest border-b border-white/10">
                <th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Plan</th><th className="p-3">Monthly</th><th className="p-3">Purchased</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/5" data-testid={`admin-user-${u.id}`}>
                    <td className="p-3 text-white/70">{u.email}</td>
                    <td className="p-3">
                      <select value={u.role} onChange={(e) => upd(setUsers)(u.id, "role", e.target.value)} className="bg-black/40 border border-white/15 px-2 py-1 text-white">
                        <option className="bg-[#0d0d0f]">user</option><option className="bg-[#0d0d0f]">admin</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <select value={u.plan} onChange={(e) => upd(setUsers)(u.id, "plan", e.target.value)} className="bg-black/40 border border-white/15 px-2 py-1 text-white">
                        {["Starter", "Explorer", "Creator", "Pro", "Enterprise"].map((p) => <option key={p} className="bg-[#0d0d0f]">{p}</option>)}
                      </select>
                    </td>
                    <td className="p-3">
                      <input value={u.monthly_credits ?? 0} onChange={(e) => upd(setUsers)(u.id, "monthly_credits", e.target.value)} className="bg-black/40 border border-white/15 px-2 py-1 w-20 text-white" />
                    </td>
                    <td className="p-3">
                      <input value={u.purchased_credits ?? 0} onChange={(e) => upd(setUsers)(u.id, "purchased_credits", e.target.value)} className="bg-black/40 border border-white/15 px-2 py-1 w-20 text-white" />
                    </td>
                    <td className="p-3 flex gap-2">
                      <button onClick={() => saveUser(u)} data-testid={`save-user-${u.id}`} className="text-neon-turquoise hover:text-white transition-colors"><Save size={15} /></button>
                      <button onClick={() => delUser(u.id)} data-testid={`del-user-${u.id}`} className="text-white/30 hover:text-neon-pink transition-colors"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HoloCard>
        )}

        {tab === "plans" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-plans">
            {plans.map((p) => (
              <HoloCard key={p.id} accent={p.accent} className="p-5" hover={false} testid={`admin-plan-${p.name}`}>
                <h3 className="font-display font-bold text-lg">{p.name}</h3>
                <label className="block mt-4 font-mono-tech text-[10px] uppercase text-white/40">Price ($)</label>
                <input value={p.price} onChange={(e) => upd(setPlans)(p.id, "price", e.target.value)} data-testid={`plan-price-${p.name}`}
                  className="mt-1 w-full bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-sm" />
                <label className="block mt-3 font-mono-tech text-[10px] uppercase text-white/40">Credits</label>
                <input value={p.credits} onChange={(e) => upd(setPlans)(p.id, "credits", e.target.value)} data-testid={`plan-credits-${p.name}`}
                  className="mt-1 w-full bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-sm" />
                <label className="block mt-3 font-mono-tech text-[10px] uppercase text-white/40">Features (one per line)</label>
                <textarea rows={4} value={Array.isArray(p.features) ? p.features.join("\n") : p.features}
                  onChange={(e) => upd(setPlans)(p.id, "features", e.target.value)}
                  className="mt-1 w-full bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-[11px] resize-none" />
                <NeonButton accent={p.accent} className="w-full mt-4" onClick={() => savePlan(p)} data-testid={`save-plan-${p.name}`}><Save size={13} /> Save</NeonButton>
              </HoloCard>
            ))}
          </div>
        )}

        {tab === "packs" && (
          <div data-testid="admin-packs">
            <div className="flex justify-end mb-4">
              <NeonButton accent="green" onClick={addPack} data-testid="admin-add-pack"><Plus size={13} /> New Pack</NeonButton>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {packs.map((pk) => (
                <HoloCard key={pk.id} accent="green" className="p-5" hover={false} testid={`admin-pack-${pk.id}`}>
                  <div className="flex items-center gap-2">
                    <Package size={16} className="text-neon-green" />
                    <input value={pk.name} onChange={(e) => upd(setPacks)(pk.id, "name", e.target.value)} className="flex-1 bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-sm" />
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <div><label className="font-mono-tech text-[9px] uppercase text-white/40">Price $</label><input value={pk.price} onChange={(e) => upd(setPacks)(pk.id, "price", e.target.value)} data-testid={`pack-price-${pk.id}`} className="mt-1 w-full bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-sm" /></div>
                    <div><label className="font-mono-tech text-[9px] uppercase text-white/40">Credits</label><input value={pk.credits} onChange={(e) => upd(setPacks)(pk.id, "credits", e.target.value)} data-testid={`pack-credits-${pk.id}`} className="mt-1 w-full bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-sm" /></div>
                    <div><label className="font-mono-tech text-[9px] uppercase text-white/40">Label</label><input value={pk.multiplier || ""} onChange={(e) => upd(setPacks)(pk.id, "multiplier", e.target.value)} className="mt-1 w-full bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-sm" /></div>
                  </div>
                  <label className="flex items-center gap-2 mt-3 font-mono-tech text-[11px] text-white/60">
                    <input type="checkbox" checked={!!pk.active} onChange={(e) => upd(setPacks)(pk.id, "active", e.target.checked)} data-testid={`pack-active-${pk.id}`} /> Active
                  </label>
                  <div className="flex gap-3 mt-4">
                    <NeonButton accent="green" className="flex-1" onClick={() => savePack(pk)} data-testid={`save-pack-${pk.id}`}><Save size={13} /> Save</NeonButton>
                    <button onClick={() => delPack(pk.id)} data-testid={`del-pack-${pk.id}`} className="text-white/30 hover:text-neon-pink transition-colors px-2"><Trash2 size={16} /></button>
                  </div>
                </HoloCard>
              ))}
            </div>
          </div>
        )}

        {tab === "settings" && (
          <HoloCard accent="yellow" className="p-6 max-w-lg" hover={false} testid="admin-settings">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Percent size={16} className="text-neon-yellow" /> Promotional Bonus</h3>
            <p className="font-mono-tech text-[11px] text-white/50 mt-1">Extra credits added on every subscription & pack purchase.</p>
            <label className="block mt-4 font-mono-tech text-[10px] uppercase text-white/40">Bonus percentage (%)</label>
            <input value={settings.promo_bonus_pct} onChange={(e) => setSettings((s) => ({ ...s, promo_bonus_pct: e.target.value }))} data-testid="settings-promo"
              className="mt-1 w-full bg-black/40 border border-white/15 px-2 py-1 text-white font-mono-tech text-sm" />
            <label className="flex items-center gap-2 mt-4 font-mono-tech text-[12px] text-white/70">
              <input type="checkbox" checked={!!settings.rollover_enabled} onChange={(e) => setSettings((s) => ({ ...s, rollover_enabled: e.target.checked }))} data-testid="settings-rollover" />
              Enable purchased-credit rollover
            </label>
            <NeonButton accent="yellow" className="mt-6" onClick={saveSettings} data-testid="save-settings"><Save size={13} /> Save Settings</NeonButton>

            <div className="h-px bg-white/10 my-8" />
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Package size={16} className="text-neon-purple" /> Creator Re-Engagement</h3>
            <p className="font-mono-tech text-[11px] text-white/50 mt-1">Send a branded campaign email + in-app notification to all creators.</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {[["character_drift", "Character drifting"], ["product_lock", "Product lock update"], ["unused_credits", "Unused credits"], ["new_features", "New features"]].map(([t, label]) => (
                <NeonButton key={t} accent="purple" onClick={() => sendReengage(t)} data-testid={`reengage-${t}`}>{label}</NeonButton>
              ))}
            </div>
          </HoloCard>
        )}

        {tab === "refunds" && (
          <HoloCard accent="green" className="overflow-x-auto" hover={false} testid="admin-refunds">
            {refunds.length === 0 ? <div className="p-10 text-center font-mono-tech text-white/30 text-sm">No refunds issued platform-wide.</div> : (
              <table className="w-full font-mono-tech text-xs min-w-[600px]">
                <thead><tr className="text-left text-white/40 uppercase tracking-widest border-b border-white/10">
                  <th className="p-3">Time</th><th className="p-3">User</th><th className="p-3">Amount</th><th className="p-3">Reason</th>
                </tr></thead>
                <tbody>
                  {refunds.map((r) => (
                    <tr key={r.id} className="border-b border-white/5">
                      <td className="p-3 text-white/60">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="p-3 text-white/50">{r.user_id.slice(-6)}</td>
                      <td className="p-3 text-neon-green">+{r.amount}</td>
                      <td className="p-3 text-white/50">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </HoloCard>
        )}
      </div>
    </div>
  );
}
